/**
 * Período de envio (ex.: 08:00–18:00). O provedor não pausa sozinho por horário, então a
 * campanha é dividida em lotes diários: cada lote cabe na janela do dia (estimativa
 * conservadora pelo delay máximo) e é agendado para o início da janela daquele dia.
 * Sem cron e sem tarefas de fundo — o provedor dispara cada lote no horário dele.
 */

export interface DisparoSendWindow {
  start: string; // "HH:MM" no fuso do navegador
  end: string; // "HH:MM"
  skip_weekends: boolean;
}

export interface DisparoBatch<T> {
  /** null = começar agora (já dentro da janela). */
  scheduledAt: Date | null;
  items: T[];
}

/** Margem por mensagem além do delay, para o tempo de envio em si. */
const PER_MESSAGE_OVERHEAD_S = 5;

function parseHm(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export function validateSendWindow(w: DisparoSendWindow): string | null {
  const s = parseHm(w.start);
  const e = parseHm(w.end);
  if (s === null || e === null) return "Informe o horário de início e fim do período";
  if (e - s < 30) return "O período de envio precisa ter pelo menos 30 minutos";
  return null;
}

function atMinutes(day: Date, minutes: number): Date {
  const d = new Date(day);
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
}

function isWeekend(d: Date) {
  const wd = d.getDay();
  return wd === 0 || wd === 6;
}

export function planDailyBatches<T>(
  items: T[],
  window: DisparoSendWindow,
  opts: { delayMaxSeconds: number; startAt: Date | null; now?: Date },
): DisparoBatch<T>[] {
  const startMin = parseHm(window.start)!;
  const endMin = parseHm(window.end)!;
  const perMsg = Math.max(1, opts.delayMaxSeconds) + PER_MESSAGE_OVERHEAD_S;
  const now = opts.now ?? new Date();
  let cursor = opts.startAt && opts.startAt > now ? new Date(opts.startAt) : new Date(now);
  const startsNow = !opts.startAt || opts.startAt <= now;

  const batches: DisparoBatch<T>[] = [];
  let remaining = [...items];
  let first = true;
  let guard = 0;

  while (remaining.length > 0 && guard++ < 400) {
    const day = new Date(cursor);
    const windowStart = atMinutes(day, startMin);
    const windowEnd = atMinutes(day, endMin);

    if ((window.skip_weekends && isWeekend(day)) || cursor >= windowEnd) {
      const next = new Date(day);
      next.setDate(next.getDate() + 1);
      cursor = atMinutes(next, startMin);
      first = false;
      continue;
    }

    const begin = cursor < windowStart ? windowStart : cursor;
    const seconds = (windowEnd.getTime() - begin.getTime()) / 1000;
    const capacity = Math.floor(seconds / perMsg) + 1;
    if (capacity < 1) {
      cursor = windowEnd;
      continue;
    }

    const immediate = first && startsNow && begin.getTime() === cursor.getTime() && cursor.getTime() === now.getTime();
    batches.push({ scheduledAt: immediate ? null : begin, items: remaining.slice(0, capacity) });
    remaining = remaining.slice(capacity);
    first = false;

    const next = new Date(day);
    next.setDate(next.getDate() + 1);
    cursor = atMinutes(next, startMin);
  }

  return batches;
}
